import React, { FC, useMemo, useRef } from 'react';
import { Header } from './Header';
import { getNetworkEnum } from '@helpers/address';
import {
  useExplorerAddressMainQuery,
  useExplorerFindAddressByHashQuery,
  useExplorerRiskCurrentQuery,
} from '@apolloGenerated';
import { amount } from '@aml/amount';
import { BigNumber } from 'bignumber.js';
import { DescriptionCategories } from '@component/downloadPDF/ui/DescriptionCategories';

import {
  Page,
  View,
  Document,
  StyleSheet,
  Image,
  PDFViewer,
  Text,
} from '@react-pdf/renderer';
import { Table } from '@component/downloadPDF/ui/Table';
import { ExplorerCategoriesTable } from '@component/explorer';
import html2canvas from 'html2canvas';
import { timestampToHumanDate } from '@utils/timestamp';

interface Props {
  address: string;
  network: string;
}

const styles = StyleSheet.create({
  body: {
    padding: 12,
  },
  title: {
    fontSize: 14,
  },
  section: {
    flexGrow: 1,
  },
  wrapper: {
    marginBottom: 20,
    flexDirection: 'column',
  },
  time: {
    fontSize: 14,
    color: '#798597',
  },
  image: {
    height: 250,
    objectFit: 'cover',
  },
});

export const Wrapper: FC<Props> = ({ address, network }) => {
  const pieRef = useRef(null);
  const variables = {
    address,
    network: getNetworkEnum(network),
  };

  const { data } = useExplorerAddressMainQuery({
    variables,
  });

  const { data: dataNode } = useExplorerFindAddressByHashQuery({
    variables,
  });

  // TRANSACTIONS
  const stats = data?.explorerAddressTransactionStats?.stats;
  const total = stats?.total || 0;
  const totalSent = stats?.sent || 0;
  const totalReceived = stats?.received || 0;

  // CLUSTER
  const cluster = data?.explorerClusterForAddress?.cluster;
  const owner = data?.explorerAddress?.owner;

  const node = dataNode?.explorerFindAddressByHash?.node;
  const formatBalance = (val?: string) =>
    Number(val) > 0 ? amount(BigNumber(val || 0).toString(10), network, false) : 0;

  const balance = formatBalance(data?.explorerBalance?.total);
  const balanceIn = formatBalance(String(data?.explorerBalance?.received));
  const balanceOut = formatBalance(String(data?.explorerBalance?.sent));

  const { data: categoryDict } = useExplorerRiskCurrentQuery({
    variables: {
      network: getNetworkEnum(network),
    },
  });

  const risk = Number(data?.explorerRisk?.risk?.risk || 0);

  const items = useMemo(() => data?.explorerRisk?.risk, [data]);

  const tableItems = useMemo(() => {
    return data?.explorerRisk.risk?.calculated?.items.map((item) => {
      const findCategory =
        categoryDict?.explorerRiskCurrent?.params &&
        Object.values(categoryDict.explorerRiskCurrent?.params).find(
          (category) => category.number === item.number,
        );

      return {
        ...item,
        colorRisk: findCategory?.risk,
      };
    });
  }, [data, categoryDict?.explorerRiskCurrent]);

  const categoryName = useMemo(() => {
    const findCategory = Object.values(
      categoryDict?.explorerRiskCurrent?.params || {},
    ).find((category) => category.number === data?.explorerRisk.risk?.reported?.number);
    return findCategory ? findCategory.name : '';
  }, [data, categoryDict?.explorerRiskCurrent]);

  const activity = data?.explorerAddressActivity?.activity;

  const image = async () => {
    if (!pieRef.current) return '';

    const canvas = await html2canvas(pieRef.current);

    return canvas.toDataURL('image/png');
  };

  const CreateDocument = () => (
    <Document>
      <Page size={'A4'} style={styles.body}>
        <View style={styles.wrapper}>
          <Text style={styles.title}>AML Risk Report</Text>
          <Text style={styles.time}>{`Generation At: ${timestampToHumanDate(
            new Date().getTime(),
          )}`}</Text>
        </View>
        <View style={styles.section}>
          <Header
            address={address}
            network={network}
            risk={risk}
            categoryName={categoryName}
            balance={{
              balance,
              balanceIn,
              balanceOut,
            }}
            tokens={data?.explorerBalance.tokens || []}
            transaction={{
              total,
              totalSent,
              totalReceived,
            }}
            cluster={cluster}
            owner={owner || null}
            activity={{ last: activity?.last, first: activity?.first }}
          />
        </View>
        <View>
          <Image src={image} style={styles.image} />
        </View>
        {categoryDict?.explorerRiskCurrent && items?.calculated?.items && (
          <>
            <Table data={categoryDict.explorerRiskCurrent} items={tableItems} />
            <DescriptionCategories
              data={categoryDict.explorerRiskCurrent}
              items={tableItems}
            />
          </>
        )}
      </Page>
    </Document>
  );

  return (
    <div style={{ overflow: 'hidden' }}>
      <PDFViewer width={'100%'} style={{ height: '100vh' }}>
        <CreateDocument />
      </PDFViewer>
      <div style={{ opacity: 0, height: 0, width: 1300 }}>
        <ExplorerCategoriesTable
          ref={pieRef}
          network={network}
          address={address}
          node={node}
          items={items?.calculated?.items}
          hasOnlyCharts
        />
      </div>
    </div>
  );
};
